import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Save, Plus, Trash2, Loader2, CheckCircle2,
  Sparkles, Copy, FileDown, Calculator,
  Upload, Wand2, X, FileText,
} from 'lucide-react';
import {
  STATUS_CONFIG, STATUS_ORDER, TRANSACTION_TYPES, RATE_TYPES,
  INCOME_FREQUENCIES, PAYMENT_FREQUENCIES, INCOME_TYPES, DOWN_PAYMENT_SOURCES, fmtMoney,
} from './mortgageConstants';
import { authedUrl } from '../../lib/authSession';
import { AmlRiskAssessmentTab } from './AmlRiskAssessmentTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'autodetect', label: 'AI Auto-Detect' },
  { id: 'details', label: 'Submission Details' },
  { id: 'mortgage', label: 'Mortgage & Property' },
  { id: 'income', label: 'Income' },
  { id: 'note', label: 'Submission Note' },
  { id: 'aml', label: 'AML Risk Assessment' },
];

// ---- tiny input helpers ----
const Field = ({ label, value, onChange, type = 'text', placeholder = '', testid }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]"
    />
  </label>
);

const ReadOnlyField = ({ label, value, suffix = '', testid }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    <input
      readOnly
      value={value === null || value === undefined || value === '' ? '—' : `${value}${suffix}`}
      data-testid={testid}
      className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-100 text-[#0A2A43] font-semibold cursor-not-allowed"
    />
  </label>
);


const SelectField = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      data-testid={testid}
      className="w-full px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#0A2A43]"
    >
      <option value="">— Select —</option>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? o : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </label>
);

const Area = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      data-testid={testid}
      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]"
    />
  </label>
);

const SectionCard = ({ title, subtitle, children, onRemove, testid }) => (
  <div className="border rounded-lg p-4 space-y-3 bg-gray-50/50" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <h4 className="font-semibold text-sm text-[#0A2A43]">{title}</h4>
        {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
      </div>
      {onRemove && (
        <button onClick={onRemove} className="text-rose-500 hover:text-rose-700" data-testid={`${testid}-remove`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
    {children}
  </div>
);

const emptyForm = () => ({
  client_name: '',
  status: 'draft',
  borrowers: [{ id: crypto.randomUUID(), name: '', relationship: 'Primary' }],
  transaction_type: '',
  credit_consent_date: '',
  agent: '',
  submission_agent: '',
  referral_source: '',
  mortgage: {},
  property: {},
  existing_mortgage: {},
  income_sources: [],
  agent_note: '',
});

// Conditional fields shown per income type
const IncomeFields = ({ inc, set, idx }) => {
  const type = inc.income_type || 'other';
  if (type === 'base_hourly') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Hourly Rate" type="number" value={inc.hourly_rate} onChange={(v) => set('hourly_rate', v)} testid={`inc-hourly-rate-${idx}`} />
          <Field label="Guaranteed Hours / period" type="number" value={inc.guaranteed_hours} onChange={(v) => set('guaranteed_hours', v)} testid={`inc-guaranteed-hours-${idx}`} />
          <SelectField label="Pay Frequency" value={inc.frequency} onChange={(v) => set('frequency', v)} options={INCOME_FREQUENCIES} testid={`inc-frequency-${idx}`} />
        </div>
        <p className="text-[11px] text-gray-500">Paystub hours breakdown (per pay period) — used to reconcile against the Letter of Employment.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Regular Hours" type="number" value={inc.regular_hours} onChange={(v) => set('regular_hours', v)} testid={`inc-regular-hours-${idx}`} />
          <Field label="Vacation Hours" type="number" value={inc.vacation_hours} onChange={(v) => set('vacation_hours', v)} testid={`inc-vacation-hours-${idx}`} />
          <Field label="Leave Hours (sick/stat/other)" type="number" value={inc.leave_hours} onChange={(v) => set('leave_hours', v)} testid={`inc-leave-hours-${idx}`} />
        </div>
        {inc.hours_note && (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`inc-hours-note-${idx}`}>
            ⚠ {inc.hours_note}
          </p>
        )}
      </div>
    );
  }
  if (type === 'salary') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Annual Salary" type="number" value={inc.annual_salary} onChange={(v) => set('annual_salary', v)} testid={`inc-annual-salary-${idx}`} />
      </div>
    );
  }
  if (type === 'two_year_average') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="Prior Year (e.g. 2024)" value={inc.prior_year} onChange={(v) => set('prior_year', v)} testid={`inc-prior-year-${idx}`} />
        <Field label="Prior Year Income" type="number" value={inc.prior_year_amount} onChange={(v) => set('prior_year_amount', v)} testid={`inc-prior-year-amount-${idx}`} />
        <div className="hidden lg:block" />
        <Field label="Latter Year (e.g. 2025)" value={inc.latter_year} onChange={(v) => set('latter_year', v)} testid={`inc-latter-year-${idx}`} />
        <Field label="Latter Year Income" type="number" value={inc.latter_year_amount} onChange={(v) => set('latter_year_amount', v)} testid={`inc-latter-year-amount-${idx}`} />
        <div className="hidden lg:block" />
        <Field label="Current YTD Amount" type="number" value={inc.ytd_amount} onChange={(v) => set('ytd_amount', v)} testid={`inc-ytd-amount-${idx}`} />
        <Field label="YTD As-of Date (latest paystub)" type="date" value={inc.ytd_as_of_date} onChange={(v) => set('ytd_as_of_date', v)} testid={`inc-ytd-date-${idx}`} />
      </div>
    );
  }
  if (type === 'self_employed') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field label="T1 Net Income — Year 1" type="number" value={inc.t1_year1_amount} onChange={(v) => set('t1_year1_amount', v)} testid={`inc-t1-year1-${idx}`} />
        <Field label="T1 Net Income — Year 2" type="number" value={inc.t1_year2_amount} onChange={(v) => set('t1_year2_amount', v)} testid={`inc-t1-year2-${idx}`} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <Field label="Annual Amount" type="number" value={inc.annual_amount} onChange={(v) => set('annual_amount', v)} testid={`inc-annual-amount-${idx}`} />
    </div>
  );
};

export const MortgageFileForm = ({ agent, fileId, onBack }) => {
  const [form, setForm] = useState(emptyForm());
  const [tab, setTab] = useState('details');
  const [loading, setLoading] = useState(!!fileId);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [autosaving, setAutosaving] = useState(false);
  const autosaveTimer = useRef(null);
  const lastSavedSnapshot = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const load = useCallback(async () => {
    if (!fileId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/mortgage/files/${fileId}`);
      if (r.ok) {
        const d = await r.json();
        const merged = { ...emptyForm(), ...d };
        setForm(merged);
        // Establish the autosave baseline so we don't re-save the freshly loaded file.
        lastSavedSnapshot.current = JSON.stringify(merged);
      }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [fileId]);

  useEffect(() => { load(); }, [load]);

  // Auto-calculate the refinance loan amount from estimated value × LTV%.
  useEffect(() => {
    const ev = Number(form.property?.estimated_value);
    const ltv = Number(form.property?.ltv_percent);
    const computed = ev > 0 && ltv > 0 ? Math.round((ev * ltv) / 100) : null;
    if ((form.property?.ltv_loan_amount ?? null) !== computed) {
      setForm((f) => ({ ...f, property: { ...(f.property || {}), ltv_loan_amount: computed } }));
    }
  }, [form.property?.estimated_value, form.property?.ltv_percent]);
  useEffect(() => {
    if (fileId || !agent || agent.id === 'admin') return;
    setForm((f) => ({
      ...f,
      agent: f.agent || agent.name || '',
      agent_phone: f.agent_phone || agent.phone || '',
      agent_email: f.agent_email || agent.email || '',
      submission_agent: f.submission_agent || agent.name || '',
    }));
  }, [fileId, agent]);

  // nested setters
  const setTop = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSub = (section, k, v) => setForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [k]: v } }));
  const setArrItem = (arr, idx, k, v) => setForm((f) => {
    const next = [...(f[arr] || [])];
    next[idx] = { ...next[idx], [k]: v };
    return { ...f, [arr]: next };
  });
  const addArrItem = (arr, item) => setForm((f) => ({ ...f, [arr]: [...(f[arr] || []), item] }));
  const removeArrItem = (arr, idx) => setForm((f) => ({ ...f, [arr]: (f[arr] || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const qs = `agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`;
      const effectiveId = fileId || form.id;
      const method = effectiveId ? 'PUT' : 'POST';
      const url = effectiveId ? `${API_URL}/api/mortgage/files/${effectiveId}?${qs}` : `${API_URL}/api/mortgage/files?${qs}`;
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const d = await r.json();
        setForm((f) => {
          const merged = { ...f, ...d };
          lastSavedSnapshot.current = JSON.stringify(merged);
          return merged;
        });
        setSavedAt(Date.now());
        setTimeout(() => setSavedAt(null), 2500);
      } else {
        let detail = `${r.status} ${r.statusText}`;
        try { const e = await r.json(); if (e?.detail) detail = typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail); } catch (parseErr) { console.warn('Could not parse save error response:', parseErr); }
        alert('Save failed: ' + detail);
      }
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally { setSaving(false); }
  };

  // Silent, debounced autosave: persists whenever the form data changes (only
  // once the file already exists — a new file must be created via "Create File").
  const autosave = useCallback(async () => {
    const cur = formRef.current;
    const effId = fileId || cur.id;
    if (!effId) return;
    const snapshot = JSON.stringify(cur);
    if (snapshot === lastSavedSnapshot.current) return;
    setAutosaving(true);
    try {
      const qs = `agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`;
      const r = await fetch(`${API_URL}/api/mortgage/files/${effId}?${qs}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: snapshot,
      });
      if (r.ok) {
        lastSavedSnapshot.current = snapshot;
        setSavedAt(Date.now());
        setTimeout(() => setSavedAt(null), 2000);
      }
    } catch (e) {
      console.warn('Autosave failed:', e);
    } finally { setAutosaving(false); }
  }, [fileId, agent]);

  // Debounce: 1.2s after the last change.
  useEffect(() => {
    const effId = fileId || form.id;
    if (!effId || loading || saving) return undefined;
    if (lastSavedSnapshot.current === null) { lastSavedSnapshot.current = JSON.stringify(form); return undefined; }
    if (JSON.stringify(form) === lastSavedSnapshot.current) return undefined;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(autosave, 1200);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [form, autosave, fileId, loading, saving]);

  const effectiveId = fileId || form.id;  // edit-mode once created
  const m = form.mortgage || {};
  const p = form.property || {};
  const em = form.existing_mortgage || {};
  const borrowerNames = (form.borrowers || []).map((b) => b.name).filter(Boolean);

  // Net Rate = Rate − Discount + Premium − Buy Down (effective basis for the mortgage).
  const _num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
  const _baseRate = _num(m.interest_rate);
  const computedNetRate = _baseRate === null
    ? null
    : Math.round((_baseRate - (_num(m.discount_rate) || 0) + (_num(m.premium_rate) || 0) - (_num(m.buy_down_rate) || 0)) * 1000) / 1000;
  useEffect(() => {
    if (computedNetRate !== (m.net_rate ?? null)) {
      setSub('mortgage', 'net_rate', computedNetRate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedNetRate]);

  // ---- Submission note handlers ----
  const [noteLength, setNoteLength] = useState('standard');
  const [noteInstr, setNoteInstr] = useState('');
  const [generating, setGenerating] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateNote = async () => {
    if (!effectiveId) { alert('Save the file first to generate a submission note.'); return; }
    setGenerating(true);
    try {
      const qs = `agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`;
      const r = await fetch(`${API_URL}/api/mortgage/files/${effectiveId}/submission-note?${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ length: noteLength, custom_instructions: noteInstr || null, agent_note: form.agent_note ?? null }),
      });
      if (r.ok) {
        const note = await r.json();
        setForm((f) => ({ ...f, submission_note: note }));
      } else {
        const e = await r.json().catch(() => ({}));
        alert('Generation failed: ' + (e.detail || r.statusText));
      }
    } catch (e) { alert('Generation failed: ' + e.message); } finally { setGenerating(false); }
  };

  const saveNote = async () => {
    if (!effectiveId) return;
    setNoteSaving(true);
    try {
      const qs = `agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`;
      const r = await fetch(`${API_URL}/api/mortgage/files/${effectiveId}/submission-note?${qs}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: form.submission_note?.content || '' }),
      });
      if (r.ok) { const note = await r.json(); setForm((f) => ({ ...f, submission_note: { ...f.submission_note, ...note } })); setSavedAt(Date.now()); setTimeout(() => setSavedAt(null), 2000); }
    } catch (e) { alert('Save failed: ' + e.message); } finally { setNoteSaving(false); }
  };

  const copyNote = async () => {
    try {
      await navigator.clipboard.writeText(form.submission_note?.content || '');
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { alert('Copy failed — select the text manually.'); }
  };

  const downloadNotePdf = () => { if (effectiveId) window.open(authedUrl(`${API_URL}/api/mortgage/files/${effectiveId}/submission-note/pdf`), '_blank'); };
  const downloadNoteDocx = () => { if (effectiveId) window.open(authedUrl(`${API_URL}/api/mortgage/files/${effectiveId}/submission-note/docx`), '_blank'); };

  // ---- AI Auto-Detect ----
  const [adFiles, setAdFiles] = useState([]);
  const [adText, setAdText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [adResult, setAdResult] = useState(null);
  const [sel, setSel] = useState({ scalar: {}, borrowers: [], income: [] });
  const [applied, setApplied] = useState(false);

  const SCALAR_LABELS = {
    client_name: 'Client / File Name', transaction_type: 'Transaction Type',
    credit_consent_date: 'Credit Consent Date', agent: 'Agent', agent_phone: 'Agent Phone',
    agent_email: 'Agent Email', submission_agent: 'Submission Agent', referral_source: 'Referral Source',
    'mortgage.term': 'Term', 'mortgage.amortization': 'Amortization', 'mortgage.rate_type': 'Rate Type',
    'mortgage.interest_rate': 'Rate',
    'mortgage.payment_frequency': 'Payment Frequency',
    'mortgage.discount_rate': 'Discount Rate', 'mortgage.premium_rate': 'Premium Rate',
    'mortgage.buy_down_rate': 'Buy Down Rate', 'mortgage.net_rate': 'Net Rate',
    'mortgage.closing_date': 'Closing Date', 'mortgage.financing_condition_deadline': 'Financing Condition Date',
    'property.address': 'Property Address', 'property.purchase_price': 'Purchase Price',
    'property.down_payment': 'Down Payment', 'property.down_payment_source': 'Down Payment Source',
    'property.estimated_value': 'Estimated Value', 'property.ltv_percent': 'Loan to Value %',
    'existing_mortgage.lender': 'Existing Lender', 'existing_mortgage.original_purchase_price': 'Original Purchase Price',
    'existing_mortgage.original_mortgage_amount': 'Original Mortgage Amount', 'existing_mortgage.balance': 'Existing Balance',
    'existing_mortgage.payment': 'Existing Payment', 'existing_mortgage.interest_rate': 'Existing Interest Rate',
    'existing_mortgage.maturity_date': 'Maturity Date', 'existing_mortgage.remaining_amortization': 'Remaining Amortization',
    'existing_mortgage.default_insurer': 'Default Insurer', 'existing_mortgage.default_insurance_ref': 'Default Insurance Ref #',
  };

  const buildScalars = (ex) => {
    const out = [];
    ['client_name', 'transaction_type', 'credit_consent_date', 'agent', 'agent_phone', 'agent_email', 'submission_agent', 'referral_source'].forEach((k) => {
      const v = ex[k];
      if (v !== null && v !== undefined && v !== '') out.push({ id: k, section: null, key: k, value: v, label: SCALAR_LABELS[k] || k });
    });
    ['mortgage', 'property', 'existing_mortgage'].forEach((section) => {
      Object.entries(ex[section] || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
          out.push({ id: `${section}.${k}`, section, key: k, value: v, label: SCALAR_LABELS[`${section}.${k}`] || k });
        }
      });
    });
    return out;
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setAdFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  const analyzeDocs = async () => {
    if (!effectiveId) { alert('Save the file first, then run auto-detect.'); return; }
    if (adFiles.length === 0 && !adText.trim()) { alert('Add at least one document or paste some text.'); return; }
    setAnalyzing(true); setApplied(false);
    try {
      const fd = new FormData();
      adFiles.forEach((f) => fd.append('files', f));
      if (adText.trim()) fd.append('pasted_text', adText.trim());
      fd.append('agent_id', agent?.id || '');
      fd.append('agent_name', agent?.name || 'Agent');
      const r = await fetch(`${API_URL}/api/mortgage/files/${effectiveId}/extract`, { method: 'POST', body: fd });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        alert('Auto-detect failed: ' + (e.detail || r.statusText));
        return;
      }
      const d = await r.json();
      setAdResult(d);
      const scalars = buildScalars(d.extracted || {});
      setSel({
        scalar: Object.fromEntries(scalars.map((s) => [s.id, true])),
        borrowers: (d.extracted?.borrowers || []).map(() => true),
        income: (d.extracted?.income_sources || []).map(() => true),
      });
    } catch (e) { alert('Auto-detect failed: ' + e.message); } finally { setAnalyzing(false); }
  };

  const applyExtracted = () => {
    const ex = adResult?.extracted || {};
    setForm((f) => {
      const next = { ...f };
      buildScalars(ex).forEach((s) => {
        if (!sel.scalar[s.id]) return;
        if (s.section) next[s.section] = { ...(next[s.section] || {}), [s.key]: s.value };
        else next[s.key] = s.value;
      });
      // borrowers
      const chosenB = (ex.borrowers || []).filter((_, i) => sel.borrowers[i]).map((b) => ({ id: crypto.randomUUID(), ...b }));
      if (chosenB.length) {
        const existing = next.borrowers || [];
        if (existing.length === 1 && !(existing[0].name || '').trim()) {
          next.borrowers = chosenB;
        } else {
          next.borrowers = [...existing, ...chosenB];
        }
      }
      // income
      const chosenInc = (ex.income_sources || []).filter((_, i) => sel.income[i]).map((e) => ({ id: crypto.randomUUID(), ...e }));
      if (chosenInc.length) next.income_sources = [...(next.income_sources || []), ...chosenInc];
      return next;
    });
    setApplied(true);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;

  const ia = form.income_analysis || {};

  return (
    <div className="space-y-4" data-testid="mortgage-file-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="mortgage-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Dashboard</Button>
          <h2 className="text-xl font-bold text-[#0A2A43]">{effectiveId ? (form.client_name || 'Mortgage File') : 'New Mortgage File'}</h2>
          {effectiveId && <span className={`text-xs font-semibold px-2 py-1 rounded-full ${(STATUS_CONFIG[form.status] || STATUS_CONFIG.draft).color}`}>{(STATUS_CONFIG[form.status] || STATUS_CONFIG.draft).label}</span>}
        </div>
        <div className="flex items-center gap-2">
          {autosaving && <span className="text-xs text-gray-400 flex items-center gap-1" data-testid="mortgage-autosaving"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>}
          {!autosaving && savedAt && <span className="text-xs text-emerald-600 flex items-center gap-1" data-testid="mortgage-saved-indicator"><CheckCircle2 className="h-4 w-4" /> Saved</span>}
          <Button onClick={save} disabled={saving} className="bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="mortgage-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {effectiveId ? 'Save Changes' : 'Create File'}
          </Button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px ${tab === tb.id ? 'border-[#0A2A43] text-[#0A2A43]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`mortgage-tab-${tb.id}`}
          >{tb.label}</button>
        ))}
      </div>

      <Card><CardContent className="p-5 space-y-4">
        {tab === 'autodetect' && (
          <div className="space-y-4" data-testid="autodetect-tab">
            <div className="bg-gradient-to-r from-[#0A2A43] to-[#0d3a5c] text-white rounded-lg p-4">
              <h3 className="font-bold flex items-center gap-2"><Wand2 className="h-5 w-5" /> AI Document Auto-Detect</h3>
              <p className="text-xs opacity-90 mt-1">Upload documents (paystub, T4, NOA, T1 General, purchase agreement…) or paste text. AI reads them and proposes values to fill the submission note — you review and apply.</p>
            </div>
            {!effectiveId && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Save the file first (give it a name), then run auto-detect.</p>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#0A2A43] transition-colors" data-testid="autodetect-dropzone">
                  <Upload className="h-7 w-7 mx-auto text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600 font-medium">Click to add documents</span>
                  <span className="block text-[11px] text-gray-400 mt-1">PDF, JPG, PNG · up to 15MB each</span>
                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onPickFiles} className="hidden" data-testid="autodetect-file-input" />
                </label>
                {adFiles.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="autodetect-file-list">
                    {adFiles.map((f, i) => (
                      <div key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center gap-2 text-xs bg-gray-50 border rounded px-2 py-1">
                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <button onClick={() => setAdFiles((p) => p.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">…or paste text (copy from an email, PDF, etc.)</span>
                <textarea value={adText} onChange={(e) => setAdText(e.target.value)} rows={7} data-testid="autodetect-paste"
                  placeholder="Paste borrower / income / property details here…"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]" />
              </div>
            </div>

            <Button onClick={analyzeDocs} disabled={analyzing || !effectiveId} className="bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="autodetect-analyze-btn">
              {analyzing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1" />}
              Analyze {adFiles.length > 0 ? `${adFiles.length} file${adFiles.length > 1 ? 's' : ''}` : 'text'}
            </Button>

            {adResult && (
              <div className="space-y-4 border-t pt-4" data-testid="autodetect-review">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-semibold text-sm text-[#0A2A43]">Review detected information</h4>
                  <Button size="sm" onClick={applyExtracted} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="autodetect-apply-btn">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Apply Selected
                  </Button>
                </div>
                {applied && <p className="text-xs text-emerald-600">Applied to the form. Review the tabs and click <b>Save Changes</b> to persist.</p>}

                {(adResult.extracted?.comments || []).length > 0 && (
                  <div className="space-y-1.5" data-testid="autodetect-comments">
                    <p className="text-xs font-medium text-amber-700 uppercase tracking-wide">Reconciliation Comments</p>
                    {adResult.extracted.comments.map((c, i) => (
                      <p key={i} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`ad-comment-${i}`}>⚠ {c}</p>
                    ))}
                  </div>
                )}

                {buildScalars(adResult.extracted || {}).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">File · Mortgage · Property</p>
                    {buildScalars(adResult.extracted || {}).map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm border rounded-lg p-2" data-testid={`ad-scalar-${s.id}`}>
                        <input type="checkbox" checked={!!sel.scalar[s.id]} onChange={(e) => setSel((pr) => ({ ...pr, scalar: { ...pr.scalar, [s.id]: e.target.checked } }))} />
                        <span className="text-gray-500 flex-1">{s.label}</span>
                        <span className="font-medium text-[#0A2A43]">{String(s.value)}</span>
                      </label>
                    ))}
                  </div>
                )}

                {(adResult.extracted?.borrowers || []).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Borrowers</p>
                    {adResult.extracted.borrowers.map((b, i) => (
                      <label key={i} className="flex items-center gap-2 text-sm border rounded-lg p-2" data-testid={`ad-borrower-${i}`}>
                        <input type="checkbox" checked={!!sel.borrowers[i]} onChange={(e) => setSel((pr) => { const n = [...pr.borrowers]; n[i] = e.target.checked; return { ...pr, borrowers: n }; })} />
                        <span className="font-medium text-[#0A2A43] flex-1">{b.name || 'Unnamed borrower'}</span>
                        <span className="text-xs text-gray-500">{b.relationship || ''}</span>
                      </label>
                    ))}
                  </div>
                )}

                {(adResult.extracted?.income_sources || []).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Income Sources</p>
                    {adResult.extracted.income_sources.map((e, i) => (
                      <label key={i} className="flex items-start gap-2 text-sm border rounded-lg p-2" data-testid={`ad-income-${i}`}>
                        <input type="checkbox" className="mt-1" checked={!!sel.income[i]} onChange={(ev) => setSel((pr) => { const n = [...pr.income]; n[i] = ev.target.checked; return { ...pr, income: n }; })} />
                        <div className="flex-1">
                          <span className="font-medium text-[#0A2A43]">{e.borrower_name || 'Borrower'} — {(e.income_type || 'income').replace(/_/g, ' ')}</span>
                          <span className="text-xs text-gray-500 block">
                            {[e.document_year, e.employer_name, e.position, e.tenure, e.annual_salary && `salary ${fmtMoney(e.annual_salary)}`, e.hourly_rate && `${fmtMoney(e.hourly_rate)}/hr`, e.latter_year_amount && `latter ${fmtMoney(e.latter_year_amount)}`, e.t1_year2_amount && `T1 Y2 ${fmtMoney(e.t1_year2_amount)}`].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {(adResult.stored_documents || []).length > 0 && (
                  <p className="text-[11px] text-gray-400">{adResult.stored_documents.length} document(s) saved to this file.</p>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'details' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Client / File Name" value={form.client_name} onChange={(v) => setTop('client_name', v)} testid="mf-client-name" />
              <SelectField label="Transaction Type" value={form.transaction_type} onChange={(v) => setTop('transaction_type', v)} options={TRANSACTION_TYPES} testid="mf-txn-type" />
              <Field label="Credit Consent Date" type="date" value={form.credit_consent_date} onChange={(v) => setTop('credit_consent_date', v)} testid="mf-credit-consent" />
              <Field label="Agent" value={form.agent} onChange={(v) => setTop('agent', v)} testid="mf-agent" />
              <Field label="Agent Phone" value={form.agent_phone} onChange={(v) => setTop('agent_phone', v)} testid="mf-agent-phone" />
              <Field label="Agent Email" type="email" value={form.agent_email} onChange={(v) => setTop('agent_email', v)} testid="mf-agent-email" />
              <Field label="Submission Agent" value={form.submission_agent} onChange={(v) => setTop('submission_agent', v)} testid="mf-submission-agent" />
              <Field label="Referral Source" value={form.referral_source} onChange={(v) => setTop('referral_source', v)} testid="mf-referral-source" />
              <SelectField label="File Status" value={form.status} onChange={(v) => setTop('status', v)} options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_CONFIG[s].label }))} testid="mf-status" />
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-sm text-[#0A2A43]">Borrowers</h4>
              {(form.borrowers || []).map((b, i) => (
                <SectionCard key={b.id || i} title={`Borrower ${i + 1}`} onRemove={form.borrowers.length > 1 ? () => removeArrItem('borrowers', i) : null} testid={`borrower-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Full Name" value={b.name} onChange={(v) => setArrItem('borrowers', i, 'name', v)} testid={`borrower-name-${i}`} />
                    <Field label="Relationship" value={b.relationship} onChange={(v) => setArrItem('borrowers', i, 'relationship', v)} placeholder="e.g. Primary, Spouse, Co-borrower" />
                  </div>
                </SectionCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrItem('borrowers', { id: crypto.randomUUID(), name: '', relationship: '' })} data-testid="add-borrower-btn"><Plus className="h-4 w-4 mr-1" /> Add Borrower</Button>
            </div>
          </div>
        )}

        {tab === 'mortgage' && (
          <div className="space-y-5">
            <SectionCard title="Mortgage Details" testid="mortgage-details-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Term" value={m.term} onChange={(v) => setSub('mortgage', 'term', v)} placeholder="e.g. 5 years" testid="mf-term" />
                <Field label="Amortization" value={m.amortization} onChange={(v) => setSub('mortgage', 'amortization', v)} placeholder="e.g. 25 years" testid="mf-amortization" />
                <SelectField label="Rate Type" value={m.rate_type} onChange={(v) => setSub('mortgage', 'rate_type', v)} options={RATE_TYPES} testid="mf-rate-type" />
                <SelectField label="Payment Frequency" value={m.payment_frequency} onChange={(v) => setSub('mortgage', 'payment_frequency', v)} options={PAYMENT_FREQUENCIES} testid="mf-payment-frequency" />
                <Field label="Closing Date" type="date" value={m.closing_date} onChange={(v) => setSub('mortgage', 'closing_date', v)} testid="mf-closing-date" />
                <Field label="Financing Condition Date" type="date" value={m.financing_condition_deadline} onChange={(v) => setSub('mortgage', 'financing_condition_deadline', v)} testid="mf-condition-date" />
              </div>
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-semibold text-[#0A2A43] mb-2">Rate Breakdown <span className="font-normal text-gray-400">— Net Rate is the effective basis for the mortgage</span></p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Field label={`Rate (%)${m.rate_type ? ` — ${m.rate_type}` : ''}`} type="number" value={m.interest_rate} onChange={(v) => setSub('mortgage', 'interest_rate', v)} placeholder="e.g. 4.79" testid="mf-interest-rate" />
                  <Field label="Discount Rate (%)" type="number" value={m.discount_rate} onChange={(v) => setSub('mortgage', 'discount_rate', v)} placeholder="e.g. 0.20" testid="mf-discount-rate" />
                  <Field label="Premium Rate (%)" type="number" value={m.premium_rate} onChange={(v) => setSub('mortgage', 'premium_rate', v)} placeholder="e.g. 0.10" testid="mf-premium-rate" />
                  <Field label="Buy Down Rate (%)" type="number" value={m.buy_down_rate} onChange={(v) => setSub('mortgage', 'buy_down_rate', v)} placeholder="e.g. 0.15" testid="mf-buy-down-rate" />
                  <ReadOnlyField label="Net Rate (%)" value={computedNetRate} testid="mf-net-rate" />
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Net Rate = Rate − Discount Rate + Premium Rate − Buy Down Rate</p>
              </div>
            </SectionCard>
            <SectionCard title="Property / Purchase Info" testid="property-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Property Address" value={p.address} onChange={(v) => setSub('property', 'address', v)} testid="mf-property-address" />
                <Field label="Purchase Price" type="number" value={p.purchase_price} onChange={(v) => setSub('property', 'purchase_price', v)} testid="mf-purchase-price" />
                <Field label="Down Payment" type="number" value={p.down_payment} onChange={(v) => setSub('property', 'down_payment', v)} testid="mf-down-payment" />
                <SelectField label="Down Payment Source" value={p.down_payment_source} onChange={(v) => setSub('property', 'down_payment_source', v)} options={DOWN_PAYMENT_SOURCES} testid="mf-down-payment-source" />
              </div>
              <div className="mt-3 pt-3 border-t">
                <p className="text-[11px] text-gray-500 mb-2">Refinance — estimated value & loan-to-value (the loan amount auto-calculates from the value and ratio).</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Field label="Estimated Value" type="number" value={p.estimated_value} onChange={(v) => setSub('property', 'estimated_value', v)} testid="mf-estimated-value" />
                  <Field label="Loan to Value (%)" type="number" value={p.ltv_percent} onChange={(v) => setSub('property', 'ltv_percent', v)} placeholder="e.g. 80" testid="mf-ltv-percent" />
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 mb-1">Calculated Loan Amount</span>
                    <div className="w-full px-3 py-2 border rounded-lg text-sm bg-emerald-50 border-emerald-200 font-semibold text-[#0A2A43]" data-testid="mf-ltv-loan-amount">
                      {p.ltv_loan_amount ? fmtMoney(p.ltv_loan_amount) : '—'}
                    </div>
                  </label>
                </div>
              </div>
            </SectionCard>
            <SectionCard title="Existing Mortgage" subtitle="For refinances, switches/transfers & HELOCs" testid="existing-mortgage-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Field label="Lender" value={em.lender} onChange={(v) => setSub('existing_mortgage', 'lender', v)} testid="mf-em-lender" />
                <Field label="Original Purchase Price" type="number" value={em.original_purchase_price} onChange={(v) => setSub('existing_mortgage', 'original_purchase_price', v)} testid="mf-em-orig-price" />
                <Field label="Original Mortgage Amount" type="number" value={em.original_mortgage_amount} onChange={(v) => setSub('existing_mortgage', 'original_mortgage_amount', v)} testid="mf-em-orig-amount" />
                <Field label="Current Balance" type="number" value={em.balance} onChange={(v) => setSub('existing_mortgage', 'balance', v)} testid="mf-em-balance" />
                <Field label="Payment" type="number" value={em.payment} onChange={(v) => setSub('existing_mortgage', 'payment', v)} testid="mf-em-payment" />
                <Field label="Interest Rate (%)" type="number" value={em.interest_rate} onChange={(v) => setSub('existing_mortgage', 'interest_rate', v)} testid="mf-em-rate" />
                <Field label="Maturity Date" type="date" value={em.maturity_date} onChange={(v) => setSub('existing_mortgage', 'maturity_date', v)} testid="mf-em-maturity" />
                <Field label="Remaining Amortization" value={em.remaining_amortization} onChange={(v) => setSub('existing_mortgage', 'remaining_amortization', v)} placeholder="e.g. 22 years 4 months" testid="mf-em-remaining-amort" />
                <Field label="Default Insurer" value={em.default_insurer} onChange={(v) => setSub('existing_mortgage', 'default_insurer', v)} placeholder="CMHC / Sagen / Canada Guaranty" testid="mf-em-insurer" />
                <Field label="Default Insurance Ref #" value={em.default_insurance_ref} onChange={(v) => setSub('existing_mortgage', 'default_insurance_ref', v)} testid="mf-em-insurance-ref" />
              </div>
            </SectionCard>
          </div>
        )}

        {tab === 'income' && (
          <div className="space-y-4" data-testid="income-tab">
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2 flex items-start gap-2">
              <Calculator className="h-4 w-4 text-[#0A2A43] mt-0.5 flex-shrink-0" />
              <span>Enter one income source per borrower. The qualifying income is computed on <b>Save</b> using the brokerage rules (base hourly, 2-year average with YTD tracking, self-employed T1 gross-up).</span>
            </p>

            {(form.income_sources || []).map((inc, i) => (
              <SectionCard key={inc.id || i} title={`Income Source ${i + 1}`} subtitle={inc.recommended_annual != null ? `Qualifying: ${fmtMoney(inc.recommended_annual)}/yr` : null} onRemove={() => removeArrItem('income_sources', i)} testid={`income-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {borrowerNames.length > 0 ? (
                    <SelectField label="Borrower" value={inc.borrower_name} onChange={(v) => setArrItem('income_sources', i, 'borrower_name', v)} options={borrowerNames} testid={`income-borrower-${i}`} />
                  ) : (
                    <Field label="Borrower Name" value={inc.borrower_name} onChange={(v) => setArrItem('income_sources', i, 'borrower_name', v)} testid={`income-borrower-${i}`} />
                  )}
                  <Field label="Label / Employer" value={inc.label} onChange={(v) => setArrItem('income_sources', i, 'label', v)} />
                  <SelectField label="Income Type" value={inc.income_type} onChange={(v) => setArrItem('income_sources', i, 'income_type', v)} options={INCOME_TYPES} testid={`income-type-${i}`} />
                  <Field label="Document Year" value={inc.document_year} onChange={(v) => setArrItem('income_sources', i, 'document_year', v)} placeholder="e.g. 2024 T4" testid={`income-doc-year-${i}`} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Field label={inc.income_type === 'self_employed' ? 'Business Name' : 'Employer Name'} value={inc.employer_name} onChange={(v) => setArrItem('income_sources', i, 'employer_name', v)} testid={`inc-employer-${i}`} />
                  <Field label="Position / Job Title" value={inc.position} onChange={(v) => setArrItem('income_sources', i, 'position', v)} placeholder="Used for the AML Schedule A check" testid={`inc-position-${i}`} />
                  <Field label="Length of Time on Job" value={inc.tenure} onChange={(v) => setArrItem('income_sources', i, 'tenure', v)} placeholder="e.g. 3 years 2 months" testid={`inc-tenure-${i}`} />
                </div>
                <IncomeFields inc={inc} idx={i} set={(k, v) => setArrItem('income_sources', i, k, v)} />
                <Area label="Notes" value={inc.notes} onChange={(v) => setArrItem('income_sources', i, 'notes', v)} />
                {inc.explanation && (
                  <p className="text-[11px] text-gray-500 bg-gray-50 border rounded p-2">{inc.method ? `${inc.method}. ` : ''}{inc.explanation}</p>
                )}
              </SectionCard>
            ))}
            <Button variant="outline" size="sm" onClick={() => addArrItem('income_sources', { id: crypto.randomUUID(), income_type: 'salary' })} data-testid="add-income-btn"><Plus className="h-4 w-4 mr-1" /> Add Income Source</Button>

            {(ia.sources || []).length > 0 && (
              <div className="rounded-lg border bg-[#0A2A43] text-white p-4 flex flex-wrap items-center justify-between gap-2 mt-2" data-testid="income-total-card">
                <span className="text-sm font-medium opacity-90">Total Qualifying Income</span>
                <span className="text-2xl font-bold">{fmtMoney(ia.total_qualifying_annual)} <span className="text-sm font-normal opacity-80">/yr · {fmtMoney(ia.total_qualifying_monthly)}/mo</span></span>
              </div>
            )}
          </div>
        )}

        {tab === 'aml' && (
          effectiveId
            ? <AmlRiskAssessmentTab key={effectiveId} fileId={effectiveId} agent={agent} clientName={form.client_name} clientFolderId={form.client_folder_id} />
            : <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="aml-save-first">Create the file first (give it a client name and borrowers), then complete the AML risk assessment.</p>
        )}
        {tab === 'note' && (
          <div className="space-y-4" data-testid="submission-note-tab">
            <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-xs text-amber-800">
              AI drafts a professional lender submission note from this file's data and the computed qualifying income. Review &amp; edit before sending — this is a preliminary broker-side summary, not a lender decision.
            </div>
            {!effectiveId && <p className="text-sm text-gray-500">Save the file once to generate a submission note.</p>}

            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-600">Agent's Notes <span className="text-gray-400">(saved with the file & always considered when generating)</span></label>
              <textarea
                value={form.agent_note ?? ''}
                onChange={(e) => setTop('agent_note', e.target.value)}
                rows={4}
                data-testid="mf-agent-note"
                placeholder="Add context the lender should know — e.g. strengths of the file, explanation of gaps, recent raise, bonus history, how income was optimized, etc. Saved with the file; click Save Changes to persist before generating."
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]"
              />
              <p className="text-[11px] text-gray-400">Tip: click <b>Save Changes</b> (top right) before generating so the latest notes are included.</p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Length</span>
                <select value={noteLength} onChange={(e) => setNoteLength(e.target.value)} data-testid="note-length-select"
                  className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#0A2A43]">
                  <option value="shorter">Shorter</option>
                  <option value="standard">Standard</option>
                  <option value="longer">Longer</option>
                </select>
              </label>
              <label className="block flex-1 min-w-[200px]">
                <span className="block text-xs font-medium text-gray-600 mb-1">Extra instructions (optional)</span>
                <input value={noteInstr} onChange={(e) => setNoteInstr(e.target.value)} data-testid="note-instructions"
                  placeholder="e.g. emphasize strong credit and stable employment"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#0A2A43]" />
              </label>
              <Button onClick={generateNote} disabled={generating || !effectiveId} className="bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="generate-note-btn">
                {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {form.submission_note ? 'Regenerate' : 'Generate'}
              </Button>
            </div>

            {form.submission_note && (
              <div className="space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs text-gray-400">
                    {form.submission_note.edited ? 'Edited' : 'Generated'}
                    {form.submission_note.generated_at ? ` · ${new Date(form.submission_note.generated_at).toLocaleString()}` : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={copyNote} data-testid="copy-note-btn">
                      {copied ? <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> : <Copy className="h-4 w-4 mr-1" />}{copied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadNotePdf} data-testid="note-pdf-btn"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                    <Button variant="outline" size="sm" onClick={downloadNoteDocx} data-testid="note-docx-btn"><FileDown className="h-4 w-4 mr-1" /> Word</Button>
                    <Button size="sm" onClick={saveNote} disabled={noteSaving} className="bg-[#0A2A43] hover:bg-[#0d3a5c] text-white" data-testid="save-note-btn">
                      {noteSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save Edits
                    </Button>
                  </div>
                </div>
                <textarea
                  value={form.submission_note.content || ''}
                  onChange={(e) => setForm((f) => ({ ...f, submission_note: { ...f.submission_note, content: e.target.value } }))}
                  rows={22}
                  data-testid="note-textarea"
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed focus:ring-2 focus:ring-[#0A2A43]"
                />
              </div>
            )}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};

export default MortgageFileForm;
